/**
 * Identity for the POM rows embedded in a grading group.
 *
 * These rows are not guaranteed to carry an `_id`: stored grading groups exist whose every row
 * has only a `code`. Keying the editor's checkbox state off `_id` gave all such rows the same
 * `undefined` identity, so ticking one row ticked every row in the group. `code` is required and
 * indexed on the schema and is unique within a group, which makes it a safe fallback identity
 * for the UI — but only `_id` can be sent to the delete endpoint, which rejects anything that is
 * not a valid ObjectId. Rows without one are reported rather than dropped.
 */
export interface GradingGroupPom {
  _id?: string | null;
  code?: string | null;
}

export interface PomDeletionPlan {
  /** Stored ids the delete endpoint can address. */
  deletableIds: string[];
  /** Codes of selected rows that carry no stored id. */
  unresolved: string[];
}

/**
 * Stable identity for one POM row.
 * @param pom The row.
 * @returns Its id, else its code, else `null` when the row can carry no identity at all.
 */
export const pomRowKey = (pom?: GradingGroupPom | null): string | null => pom?._id ?? pom?.code ?? null;

/**
 * Splits a selection into the rows delete can address and the rows it cannot.
 * @param selectedKeys Row keys currently ticked, as produced by `pomRowKey`.
 * @param poms The persisted POM rows of the grading group.
 * @returns The addressable ids and the codes of the rows that have no stored id.
 */
export const resolvePomDeletion = (
  selectedKeys?: Array<string | null | undefined> | null,
  poms?: GradingGroupPom[] | null,
): PomDeletionPlan => {
  const rows = Array.isArray(poms) ? poms : [];
  const plan: PomDeletionPlan = { deletableIds: [], unresolved: [] };

  (Array.isArray(selectedKeys) ? selectedKeys : []).forEach((key) => {
    if (key === null || key === undefined) {
      return;
    }
    const row = rows.find((pom) => pomRowKey(pom) === key);
    if (!row) {
      return;
    }
    if (row._id) {
      plan.deletableIds.push(row._id);
      return;
    }
    if (row.code) {
      plan.unresolved.push(row.code);
    }
  });

  return plan;
};
